import React, { useState, useRef, useEffect, useCallback } from "react";
import { 
  Box, 
  FormControl, 
  Input, 
  FormHelperText, 
  Button, 
  Avatar, 
  SvgIcon,
  Typography,
  Stack
} from "@mui/joy";
import { isValidPhoneNumber, AsYouType } from "libphonenumber-js";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import "cropperjs/dist/cropper.css";
import { v4 as uuidv4 } from "uuid";

import { supabase } from "../../supabaseClient";
import { useToast } from "../../context/ToastContext";
import { useModal } from "../../context/ModalContext";
import { getInitials, getProfilePictureName, deleteProfilePicture } from "../../utils";
import { styled } from "@mui/joy";
import { Class } from "../../types";
import ImageCropModal from "./ImageCropModal";

const VisuallyHiddenInput = styled("input")`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

interface Teacher {
  id: string;
  first_name: string;
  last_name: string;
  email_address: string;
  phone_number: string;
  profile_picture?: string | null;
  profile_picture_id: string | null;
  primary_classes: Class[];
  secondary_classes: Class[];
}

interface AddEditTeacherModalProps {
  teacher?: Teacher;
  onTeacherUpdated?: () => void;
}

const AddEditTeacherModal: React.FC<AddEditTeacherModalProps> = ({ teacher, onTeacherUpdated }) => {
  const { closeModal, setModalTitle, registerSubmitCallback } = useModal();
  const { showToast } = useToast();

  const [formValues, setFormValues] = useState({
    first_name: teacher?.first_name || "",
    last_name: teacher?.last_name || "",
    email_address: teacher?.email_address || "",
    phone_number: teacher?.phone_number || "",
  });

  const [errors, setErrors] = useState<{ email: string; phone: string }>({
    email: "",
    phone: "",
  });

  const [imagePreview, setImagePreview] = useState<string | null>(teacher?.profile_picture || null);
  const [openCropModal, setOpenCropModal] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const imagePreviewRef = useRef<string | null>(null);

  const validateForm = useCallback(() => {
    const newErrors = { email: "", phone: "" };
    let isValid = true;

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formValues.email_address)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    // Validate phone number
    if (!isValidPhoneNumber(formValues.phone_number, "US")) {
      newErrors.phone = "Please enter a valid phone number";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  }, [formValues.email_address, formValues.phone_number]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    let formattedValue = value;

    if (name === "phone_number") {
      formattedValue = new AsYouType("US").input(value);
    }

    setFormValues((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempImageUrl(reader.result as string);
        setOpenCropModal(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCrop = async (croppedImage: string) => {
    setImagePreview(croppedImage);
    imagePreviewRef.current = croppedImage;
  };

  const handleSave = useCallback(async () => {
    if (!validateForm()) return;

    try {
      let profile_picture_id = teacher?.profile_picture_id;
      let newProfilePictureUUID = null;

      // Handle profile picture upload if there's a new image
      if (imagePreviewRef.current) {
        newProfilePictureUUID = uuidv4();
        const croppedBlob = await (await fetch(imagePreviewRef.current)).blob();
        const filePath = getProfilePictureName("teachers", newProfilePictureUUID);

        // Delete old profile picture if it exists
        if (teacher?.profile_picture_id) {
          await deleteProfilePicture("teachers", teacher.profile_picture_id);
        }

        // Upload new profile picture
        const { error: uploadError } = await supabase.storage
          .from("profile-pictures")
          .upload(filePath, croppedBlob, {
            cacheControl: "0",
            upsert: true,
          });

        if (uploadError) {
          throw new Error(`Failed to upload profile picture: ${uploadError.message}`);
        }

        profile_picture_id = newProfilePictureUUID;
      }

      const teacherData = {
        ...formValues,
        profile_picture_id,
      };

      let error;
      if (teacher?.id) {
        // Update existing teacher
        ({ error } = await supabase
          .from("teachers")
          .update(teacherData)
          .eq("id", teacher.id));
      } else {
        // Insert new teacher
        ({ error } = await supabase
          .from("teachers")
          .insert([teacherData]));
      }

      if (error) throw error;

      showToast(
        teacher ? "Teacher updated successfully!" : "Teacher added successfully!",
        "success",
        "solid"
      );
      
      if (onTeacherUpdated) {
        onTeacherUpdated();
      }
      
      closeModal();
    } catch (error) {
      console.error("Error saving teacher:", error);
      showToast(error instanceof Error ? error.message : "Error saving teacher.", "danger", "solid");
    }
  }, [teacher, formValues, imagePreviewRef, showToast, closeModal, onTeacherUpdated, validateForm]);

  // Register the submit callback
  useEffect(() => {
    setModalTitle(teacher ? "Edit Teacher" : "Add Teacher");
    registerSubmitCallback(handleSave);
  }, [teacher, registerSubmitCallback, handleSave, setModalTitle]);

  return (
    <>
      <Box sx={{ mt: 2, mb: 4 }}>
        <Stack spacing={3}>
          {/* Profile Picture Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={imagePreview || undefined}
              alt="Profile Picture"
              sx={{ width: 100, height: 100 }}
            >
              {getInitials(formValues) || "HC"}
            </Avatar>
            <Button
              component="label"
              variant="outlined"
              color="neutral"
              startDecorator={
                <SvgIcon>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"
                    />
                  </svg>
                </SvgIcon>
              }
            >
              Upload a Photo
              <VisuallyHiddenInput type="file" onChange={handleFileChange} />
            </Button>
          </Box>

          {/* Form Fields */}
          <Stack spacing={2}>
            <Typography level="title-lg">Personal Information</Typography>
            
            <FormControl>
              <Input
                size="lg"
                placeholder="First Name"
                name="first_name"
                value={formValues.first_name}
                onChange={handleInputChange}
              />
            </FormControl>

            <FormControl>
              <Input
                size="lg"
                placeholder="Last Name"
                name="last_name"
                value={formValues.last_name}
                onChange={handleInputChange}
              />
            </FormControl>

            <Typography level="title-lg" sx={{ mt: 2 }}>Contact Information</Typography>

            <FormControl error={Boolean(errors.email)}>
              <Input
                size="lg"
                placeholder="Email Address"
                name="email_address"
                value={formValues.email_address}
                onChange={handleInputChange}
              />
              {errors.email && (
                <FormHelperText>
                  <InfoOutlined /> {errors.email}
                </FormHelperText>
              )}
            </FormControl>

            <FormControl error={Boolean(errors.phone)}>
              <Input
                size="lg"
                placeholder="Phone Number"
                name="phone_number"
                value={formValues.phone_number}
                onChange={handleInputChange}
              />
              {errors.phone && (
                <FormHelperText>
                  <InfoOutlined /> {errors.phone}
                </FormHelperText>
              )}
            </FormControl>
          </Stack>
        </Stack>
      </Box>

      <ImageCropModal
        open={openCropModal}
        initialImage={tempImageUrl}
        onClose={() => setOpenCropModal(false)}
        onCrop={handleCrop}
      />
    </>
  );
};

export default AddEditTeacherModal; 